import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { map, Observable, tap } from 'rxjs';

import { environment } from '@env/environment';
import {
  IAuthResponse,
  IForgotPasswordDto,
  IForgotPasswordResponse,
  ILoginDto,
  IResetPasswordDto,
  IResetPasswordResponse,
} from '@core/models/auth';
import { IResponse } from '@core/models/general';

import { AuthTokenService } from './auth-token.service';
import { ConfirmationService } from 'primeng/api';
import { AppStateService } from './app-state.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(AuthTokenService);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly appStateService = inject(AppStateService);

  readonly FORGOT_PASSWORD_TOKEN_KEY = 'forgot-password-token';

  readonly url = `${environment.api.baseUrl}/auth`;

  login(body: ILoginDto): Observable<IResponse<IAuthResponse>> {
    return this.http.post<IResponse<IAuthResponse>>(`${this.url}/login`, body).pipe(
      tap(async response => {
        this.storage.setAuthToken(response.data.accessToken);
        this.appStateService.loadInitialState();
        const returnUrl: string = this.route.snapshot.queryParams['returnUrl'] || '/';
        const url = new URL(returnUrl, window.location.origin);
        await this.router.navigate([url.pathname], {
          replaceUrl: true,
          queryParams: { ...Object.fromEntries(url.searchParams) },
        });
      })
    );
  }

  forgotPassword(body: IForgotPasswordDto): Observable<IForgotPasswordResponse> {
    return this.http
      .post<IResponse<IForgotPasswordResponse>>(`${this.url}/forgot-password`, body)
      .pipe(
        tap(() => {
          this.showMessageForgotPassword(body.email);
        }),
        map(response => response.data)
      );
  }

  resetPassword(body: IResetPasswordDto): Observable<IResetPasswordResponse> {
    return this.http
      .post<IResponse<IResetPasswordResponse>>(`${this.url}/reset-password`, body)
      .pipe(
        tap(() => {
          this.showMessageResetPasswordSuccess();
        }),
        map(response => response.data)
      );
  }

  async logout() {
    this.storage.deleteAll();
    this.appStateService.clear();
    await this.router.navigate(['/login'], { replaceUrl: true });
  }

  showMessageForgotPassword(email: string): void {
    const htmlMessage: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<p>Te hemos enviado un código de verificación a tu correo <strong class="font-semibold text-primary">${email}</strong> para restablecer su contraseña.</p>`
    );
    this.confirmationService.confirm({
      key: 'avisoDialog',
      message: htmlMessage as string,
      header: 'Cambiar contraseña',
      icon: 'pi pi-info-circle',
      acceptVisible: false,
      rejectLabel: 'Aceptar',
      rejectButtonProps: { label: 'Aceptar', severity: 'secondary', outlined: true, focus: true },
    });
  }

  showMessageResetPasswordSuccess(): void {
    const htmlMessage: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<p>Tu contraseña fue actualizada correctamente.</p>`
    );
    this.confirmationService.confirm({
      key: 'avisoDialog',
      message: htmlMessage as string,
      header: 'Contraseña actualizada',
      icon: 'pi pi-info-circle',
      acceptVisible: false,
      rejectLabel: 'Aceptar',
      rejectButtonProps: {
        label: 'Aceptar',
        severity: 'secondary',
        outlined: true,
        focus: true,
      },
    });
  }
}
